import { Autocomplete, Box, MenuItem, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash';
import { useId, useState } from 'react';

import { FormField, api, getQueryProps, useDebounce } from 'common';
import { type Flow, FlowDefinition } from 'vivaa-api';

import { useFlowDefinitionContext } from '../../../../../../context.ts';
import { useReactFlowViewContext } from '../../../../../context/context.tsx';
import { FlowKind } from '../../../constants.tsx';
import { useSubFlowFieldsContext } from '../context.ts';

/**
 * Must be wrapped in a SubFlowFieldsProvider
 */
export function FlowAutocomplete() {
  const {
    flowIdFieldProps,
    specificVersionFlowDefinitionIdFieldProps,
    selectedSubflow,
    allowedFlowKinds,
  } = useSubFlowFieldsContext();
  const { visibleError: visibleFlowIdError, setValue: setFlowId } = flowIdFieldProps;
  const { setValue: setFlowDefinitionId } = specificVersionFlowDefinitionIdFieldProps;
  const { isEditable } = useReactFlowViewContext();
  const { flowDefinition } = useFlowDefinitionContext();
  const [inputValue, setInputValue] = useState('');
  const debouncedValue = useDebounce(inputValue, 300);

  const enabledFlowKinds = new Set<FlowKind>();
  if (isEditable) {
    if (allowedFlowKinds.includes(FlowKind.Base)) {
      enabledFlowKinds.add(FlowKind.Base);
    }
    if (allowedFlowKinds.includes(FlowKind.Skill)) {
      enabledFlowKinds.add(FlowKind.Skill);
    }
  }
  const { data: flows, isPending: isFlowsPending } = useQuery({
    ...getQueryProps(api.client.Flow, 'findAll', {
      // Restrict to flows within the same organization (across agent is ok)
      organizationId: flowDefinition.organizationId,
      kind: api.Flow.FlowKind.Base,
      orderBy: {
        field: api.Flow.OrderByField.Name,
        direction: 'asc',
      },
      q: debouncedValue,
      limit: 100,
    }),
    enabled: enabledFlowKinds.has(FlowKind.Base),
    select: (data) => {
      return enabledFlowKinds.has(FlowKind.Base)
        ? data.filter((flow) => flow.id !== flowDefinition.flowId)
        : [];
    },
  });

  const { data: skills, isPending: isSkillsPending } = useQuery({
    ...getQueryProps(api.client.FlowBlock, 'findAllSkills', {
      orderBy: {
        field: api.Flow.OrderByField.Name,
        direction: 'asc',
      },
      q: debouncedValue,
      limit: 100,
    }),
    enabled: enabledFlowKinds.has(FlowKind.Skill),
    select: (data) => {
      return enabledFlowKinds.has(FlowKind.Skill)
        ? data.filter((flow) => flow.id !== flowDefinition.flowId)
        : [];
    },
  });

  const fieldId = useId();
  return (
    <>
      <FormField id={fieldId} label="Subflow" error={visibleFlowIdError}>
        <Autocomplete<Pick<Flow.Flow, 'id' | 'name' | 'kind'>>
          id={fieldId}
          multiple={false}
          options={[...(flows ?? []), ...(skills ?? [])]}
          loading={
            (enabledFlowKinds.has(FlowKind.Base) && isFlowsPending) ||
            (enabledFlowKinds.has(FlowKind.Skill) && isSkillsPending)
          }
          disabled={!isEditable}
          value={selectedSubflow ?? null}
          onChange={async (_, newFlow) => {
            if (newFlow) {
              setFlowId(newFlow.id);
              try {
                // Pick a new flow definition. Try to pick the published version,
                // if that doesn't exist, pick the pre-published version
                const subflowWithDefinitions =
                  newFlow.kind === FlowKind.Skill
                    ? await api.client.FlowBlock.findOneSkillById(newFlow.id)
                    : await api.client.Flow.findOneById(newFlow.id);

                const flowDefinitions: Pick<
                  api.FlowDefinition.FlowDefinition,
                  'id' | 'version' | 'state'
                >[] = subflowWithDefinitions?.flowDefinitions ?? [];
                const publishedFlowDefinition = sortBy(flowDefinitions, 'version')
                  .reverse()
                  .find((fd) => fd.state === api.FlowDefinition.FlowDefinitionState.Published);
                const prePublishedFlowDefinition = flowDefinitions.find(
                  (fd) => FlowDefinition.FlowDefinitionStateProps[fd.state].isPrePublishState
                );
                setFlowDefinitionId((publishedFlowDefinition ?? prePublishedFlowDefinition)?.id);
              } catch (error) {
                console.error('Error finding flow definitions', error);
                setFlowDefinitionId(undefined);
              }
            }
          }}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="Enter flow name" />
          )}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options) => options}
          renderOption={(listItemProps, option) => (
            <MenuItem {...listItemProps} key={option.id}>
              <Box display="flex" justifyContent="space-between" width="100%">
                <span>{option.name}</span>
              </Box>
            </MenuItem>
          )}
        />
      </FormField>
    </>
  );
}
